<script setup lang="ts">
import { SelectProps, getAriaIDs } from './inputHelper'

const props = defineProps<SelectProps>()
const ariaIds = getAriaIDs(props)
const model = defineModel()
const isEmpty = computed(() => model.value === '')
const { t } = useI18n()
const defaultOption = props.default ?? t('product.selector.option_select_text')
</script>

<template>
  <div class="input--select" :class="{ 'input--not-empty': !isEmpty, 'input--error': error }">
    <label>
      <span>{{ label }}{{ required ? ' *' : '' }}</span>
      <select
        v-model="model"
        v-bind="$attrs"
        :name
        :required="required"
        :data-testid="selectTestId"
        :aria-describedby="ariaIds"
        :aria-invalid="!!error"
      >
        <option value="" selected hidden disabled>
          {{ defaultOption }}
        </option>
        <option v-for="option in options" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </label>
    <InputMessage :name="name" :error="error" :hint="hint" :attempt="attempt" />
  </div>
</template>

<style lang="postcss">
.input--select {
  position: relative;

  select {
    appearance: none;
    border-radius: 0;
    background-color: transparent;
    min-height: 3.125rem;
    outline-offset: 0.2rem;
    width: 100%;
    display: inline-block; /* required for scale */
    padding: 1rem 0.5rem 0.2rem 1rem;
    transition: var(--transition-0);
    border: 1px solid var(--border-input);
    color: currentColor;
    outline: none;

    &:focus,
    &:focus-visible {
      border: 2px solid var(--status-info);
    }
  }

  label span {
    position: absolute;
    left: 1.05rem;
    top: 0.4rem;
    color: var(--text-tertiary);
    font-size: var(--text-small);
    transition: var(--transition-0);
  }

  &:after {
    content: '';
    background: url('~/assets/sprite/svg/symbol-arrow-down.svg') no-repeat;
    width: 1.5rem;
    height: 1.5rem;
    display: block;
    position: absolute;
    top: 0;
    right: 0.5rem;
    translate: 0% 50%;
    transition: var(--transition-1);
    pointer-events: none;
  }
}
</style>
